<template>
  <div class="v2026-tm-anchor" @keydown.esc="onEsc" @focusout="onFocusOut">
    <!-- Same class list as the b-dropdown toggle it replaces: the owner's ::v-deep
         .v2026-pill__toggle rule and bootstrap's global .btn base must both keep applying. -->
    <button type="button"
            ref="triggerBtn"
            class="btn btn-link btn-sm v2026-pill__toggle"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="panelId"
            @click.prevent="toggle"
            @keydown.down.prevent="onTriggerArrow('first')"
            @keydown.up.prevent="onTriggerArrow('last')">
      <span class="v2026-pill__lead" aria-hidden="true"
            :style="{ '-webkit-mask-image': 'url(' + leadIcon + ')', 'mask-image': 'url(' + leadIcon + ')' }"></span>
      <span class="v2026-pill__label">{{ label }}</span>
      <i class="fas fa-chevron-down v2026-pill__chev" aria-hidden="true"></i>
    </button>

    <!-- :renderKey is mandatory — the rows are method-driven state that does NOT flow through
         props, so Vue 2.7's stable-slot optimisation would freeze them. The CURRENT SELECTION
         must be part of the key, or picking an item leaves the open menu showing the old one. -->
    <OverlayCard :id="panelId"
                 class="v2026-tm-pos"
                 :class="config.align === 'right' ? 'v2026-tm-pos--right' : null"
                 :style="{ width: config.width + 'px' }"
                 :open="open"
                 v-show="open"
                 :ignore="triggerEl"
                 :renderKey="renderKey"
                 @close="hide()">
      <template #default="rk">
        <div class="v2026-tm-list"
             ref="list"
             role="listbox"
             tabindex="-1"
             :aria-label="config.menuAriaLabel"
             :data-rk="rk.dirty">
          <div v-for="group in groups"
               :key="group.key"
               class="v2026-tm-group"
               :role="group.header ? 'group' : null"
               :aria-label="group.header || null">
            <!-- A non-selectable parent is the NAME OF A GROUP, not a disabled option:
                 no role, no tabindex, no hover, no pointer. -->
            <div v-if="group.header" class="v2026-tm-header" :title="group.header" lang="th">{{ group.header }}</div>
            <div v-for="row in group.rows"
                 :key="row.key"
                 class="v2026-tm-row"
                 :class="{ 'v2026-tm-row--child': row.level > 0, 'v2026-tm-row--on': row.selected }"
                 role="option"
                 :data-idx="row.idx"
                 :tabindex="row.idx === activeIndex ? 0 : -1"
                 :aria-selected="marksSelection ? (row.selected ? 'true' : 'false') : null"
                 :title="row.item.name"
                 :style="row.color ? { color: row.color } : null"
                 @click="onPick(row)"
                 @keydown="onRowKeydown($event, row)">
              <i v-if="config.rowIcon" :class="config.rowIcon" class="v2026-tm-row__ico" aria-hidden="true"></i>
              <span class="v2026-tm-row__name" lang="th">{{ row.item.name }}</span>
              <i v-if="row.selected" class="fas fa-check v2026-tm-row__check" aria-hidden="true"></i>
            </div>
          </div>
          <div v-if="!rowCount" class="v2026-tm-empty">{{ config.emptyText }}</div>
        </div>

        <!-- archiveHint ships null on every variant. The path stays so it can be switched on
             from the config alone. -->
        <p v-if="config.archiveHint && rowCount" class="v2026-tm-hint">{{ config.archiveHint }}</p>

        <button v-if="config.footerLabel"
                type="button"
                ref="footBtn"
                class="v2026-tm-foot"
                @click="onFooter">
          <span v-if="config.footerIcon" class="v2026-tm-foot__ico" aria-hidden="true"
                :style="{ '-webkit-mask-image': 'url(' + config.footerIcon + ')', 'mask-image': 'url(' + config.footerIcon + ')' }"></span>
          <span>{{ config.footerLabel }}</span>
        </button>
      </template>
    </OverlayCard>
  </div>
</template>

<script>
import OverlayCard from '@/components/coversation/OverlayCard'

const SETTINGS_ICON = require('@/assets/icons2026/settings.svg')

// The three toolbar menus differ only in presentation. One component, three frozen records —
// same shape as RAIL_CONFIG / STATUS_ICON_CATALOG elsewhere in this codebase.
export const TOPIC_MENU_VARIANTS = Object.freeze({
  systemTag: Object.freeze({
    childKey: 'child_tags',
    // Hides the parent row only: its children and its group still render (legacy quirk).
    skipZeroIdParents: true,
    skipZeroIdItems: false,
    gateParentsOnCanSelect: true,
    parentColor: '#222222',
    childColorKey: 'color',
    rowIcon: null,
    footerLabel: 'ตั้งค่าหัวข้อสนทนา',
    footerIcon: SETTINGS_ICON,
    menuAriaLabel: 'เลือกหัวข้อสนทนา',
    emptyText: 'ยังไม่มีหัวข้อสนทนา',
    selectionMode: 'single',
    archiveHint: null,
    align: 'left',
    width: 300
  }),
  chatTopic: Object.freeze({
    childKey: 'child_chat_topics',
    skipZeroIdParents: false,
    skipZeroIdItems: false,
    gateParentsOnCanSelect: true,
    parentColor: null,
    childColorKey: null,
    rowIcon: null,
    // No footer at all — not an empty bar, not a hairline.
    footerLabel: null,
    footerIcon: null,
    menuAriaLabel: 'เลือกหัวข้อสนทนา',
    emptyText: 'ยังไม่มีหัวข้อสนทนา',
    selectionMode: 'single',
    archiveHint: null,
    align: 'left',
    width: 300
  }),
  tag: Object.freeze({
    // null ⇒ flat list
    childKey: null,
    skipZeroIdParents: false,
    skipZeroIdItems: true,
    gateParentsOnCanSelect: false,
    parentColor: null,
    childColorKey: 'color',
    rowIcon: 'fas fa-tag',
    footerLabel: 'ตั้งค่าแท็ก',
    footerIcon: SETTINGS_ICON,
    menuAriaLabel: 'เลือกแท็ก',
    emptyText: 'ยังไม่มีแท็ก',
    // Tags are additive, so a single-selection marker would be a lie.
    selectionMode: 'none',
    archiveHint: null,
    align: 'left',
    width: 300
  })
})

export default {
  name: 'topic-menu-v2026',
  components: {
    OverlayCard
  },
  props: {
    // Straight from Vuex. ALL THREE SOURCES START AS null — never call .length on it unguarded.
    items: {
      type: Array,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    leadIcon: {
      type: String,
      default: null
    },
    config: {
      type: Object,
      required: true
    },
    // Suffixes the panel id: menus 2 and 3 can be in the DOM at the same time.
    variant: {
      type: String,
      required: true
    },
    selectedId: {
      type: [Number, String],
      default: null
    },
    // A PRIMITIVE, so the watcher fires only on a real conversation change.
    conversationId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      open: false,
      // Roving tabindex cursor over the selectable rows. -1 = nothing focusable in the list.
      activeIndex: -1,
      // Handed to OverlayCard as :ignore, so clicking the trigger while open closes it once.
      triggerEl: null
    }
  },
  computed: {
    panelId () {
      return 'v2026-tm-' + this.variant.toLowerCase()
    },
    marksSelection () {
      return this.config.selectionMode === 'single'
    },
    groups () {
      const cfg = this.config
      const items = this.items || []
      const out = []
      let idx = 0

      if (!cfg.childKey) {
        const rows = []
        items.forEach((item, i) => {
          if (cfg.skipZeroIdItems && item.id === 0) return
          rows.push(this.buildRow(item, 0, cfg.childColorKey ? item[cfg.childColorKey] : null, idx++, 'f' + i))
        })
        if (rows.length) out.push({ key: 'flat', header: null, rows })
        return out
      }

      items.forEach((parent, i) => {
        const hideParent = cfg.skipZeroIdParents && parent.id === 0
        const isHeader = cfg.gateParentsOnCanSelect && !parent.can_select
        let header = null
        const rows = []
        if (!hideParent) {
          if (isHeader) header = parent.name
          else rows.push(this.buildRow(parent, 0, cfg.parentColor, idx++, i + 'p'))
        }
        const children = parent[cfg.childKey] || []
        children.forEach((child, k) => {
          rows.push(this.buildRow(child, 1, cfg.childColorKey ? child[cfg.childColorKey] : null, idx++, i + 'c' + k))
        })
        if (header || rows.length) out.push({ key: 'g' + i, header, rows })
      })
      return out
    },
    flatRows () {
      return this.groups.reduce((acc, group) => acc.concat(group.rows), [])
    },
    rowCount () {
      return this.flatRows.length
    },
    // Signature of EVERYTHING the slot renders. Anything left out of this string freezes on screen
    // behind Vue 2.7's stable-slot optimisation — the row keys already carry id + selected flag.
    renderKey () {
      const sig = this.groups
        .map(group => (group.header || '') + '>' + group.rows.map(row => row.key).join(','))
        .join(';')
      return String(this.selectedId) + '#' + sig + '#' + (this.open ? 1 : 0) + '#' + this.activeIndex
    }
  },
  watch: {
    conversationId () {
      this.hide()
    }
  },
  mounted () {
    this.triggerEl = this.$refs.triggerBtn || null
  },
  methods: {
    buildRow (item, level, color, idx, seed) {
      const selected = this.marksSelection && this.selectedId != null && item.id === this.selectedId
      return {
        key: seed + '#' + item.id + '|' + (selected ? 1 : 0),
        item: item,
        level: level,
        color: color,
        idx: idx,
        selected: selected
      }
    },
    // --- public, b-dropdown-compatible API -----------------------------------------------
    show () {
      if (this.open) return
      this.open = true
      this.$emit('open')
      this.activeIndex = this.initialActiveIndex()
      this.$nextTick(() => this.focusActive())
    },
    hide (returnFocus = false) {
      this.open = false
      if (returnFocus) {
        this.$nextTick(() => {
          // The node can be gone: picking a topic archives the case, which can clear the
          // conversation and destroy this component before the tick runs.
          if (this.$refs.triggerBtn) this.$refs.triggerBtn.focus()
        })
      }
    },
    toggle () {
      if (this.open) this.hide(true)
      else this.show()
    },
    // --- keyboard / focus ----------------------------------------------------------------
    initialActiveIndex () {
      const rows = this.flatRows
      if (!rows.length) return -1
      const selected = rows.findIndex(row => row.selected)
      return selected === -1 ? 0 : selected
    },
    focusActive () {
      const list = this.$refs.list
      if (!list) return
      const el = this.activeIndex >= 0 ? list.querySelector('[data-idx="' + this.activeIndex + '"]') : null
      if (el) {
        el.focus({ preventScroll: true })
        el.scrollIntoView({ block: 'nearest' })
        return
      }
      // Never drop focus to document.body — focus-out close would stop working.
      if (this.$refs.footBtn) this.$refs.footBtn.focus({ preventScroll: true })
      else list.focus({ preventScroll: true })
    },
    moveTo (index) {
      const rows = this.flatRows
      if (!rows.length) return
      this.activeIndex = Math.max(0, Math.min(rows.length - 1, index))
      this.$nextTick(() => this.focusActive())
    },
    onTriggerArrow (where) {
      if (!this.open) this.show()
      this.$nextTick(() => {
        this.moveTo(where === 'last' ? this.flatRows.length - 1 : 0)
      })
    },
    onRowKeydown (e, row) {
      const key = e.key
      if (key === 'ArrowDown') {
        e.preventDefault()
        this.moveTo(row.idx + 1)
      } else if (key === 'ArrowUp') {
        e.preventDefault()
        this.moveTo(row.idx - 1)
      } else if (key === 'Home') {
        e.preventDefault()
        this.moveTo(0)
      } else if (key === 'End') {
        e.preventDefault()
        this.moveTo(this.flatRows.length - 1)
      } else if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        e.preventDefault()
        this.onPick(row)
      }
    },
    onEsc (e) {
      if (!this.open) return
      e.stopPropagation()
      this.hide(true)
    },
    onFocusOut (e) {
      if (!this.open) return
      // relatedTarget is null when the WINDOW loses focus (Alt-Tab) or a non-focusable area is
      // clicked. Closing there would make the menu vanish on Alt-Tab; the real outside-click case
      // is already covered by OverlayCard's document mousedown listener.
      if (!e.relatedTarget) return
      if (this.$el.contains(e.relatedTarget)) return
      this.hide()
    },
    // --- selection -----------------------------------------------------------------------
    onPick (row) {
      // The owner's handler closes the menu (this.$refs.<name>.hide(true)), exactly as it did
      // with b-dropdown. Do not close here or the focus return fires twice.
      this.$emit('select', row.item)
    },
    onFooter () {
      this.hide()
      this.$emit('footer')
    }
  }
}
</script>

<style scoped>
.v2026-tm-anchor {
  /* reproduces bootstrap's .btn-group, which is what the b-dropdown root used to be */
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  flex-shrink: 0;
}

/* The owner's ::v-deep rule wins on every shared declaration; these are here so the trigger is
   still correct if the component is ever mounted outside .v2026-pill--topic. */
.v2026-tm-anchor .v2026-pill__toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-1, 4px);
  height: var(--v2026-ctrl-h, 36px);
  min-height: var(--v2026-ctrl-h, 36px);
  line-height: 1;
  padding: 0 var(--v2026-sp-2, 8px);
  border: 1px solid var(--v2026-pill-border, #EBEDF0);
  border-radius: 4px;
  background: var(--v2026-surface, #FFFFFF);
  color: var(--v2026-text-secondary, #667085);
  font-size: var(--v2026-fs-ctrl, 13px);
  text-decoration: none;
  box-shadow: none;
}

.v2026-tm-anchor .v2026-pill__toggle:hover,
.v2026-tm-anchor .v2026-pill__toggle:focus,
.v2026-tm-anchor .v2026-pill__toggle:active {
  background: var(--v2026-surface-hover, #E9ECF1);
  color: var(--v2026-text, #101828);
  box-shadow: none;
}

/* Re-declared, NOT inherited: the owner's .v2026-pill__lead / __label / __chev rules carry the
   owner's data-v-* attribute and cannot reach a child component's internals. */
.v2026-pill__lead {
  width: 16px;
  height: 16px;
  display: inline-block;
  flex-shrink: 0;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon, #475467);
}

.v2026-pill__label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--v2026-text-secondary, #667085);
}

.v2026-pill__chev {
  font-size: 10px;
  color: var(--v2026-text-muted, #98A2B3);
}

/* Written with the .v2026-tm-anchor ancestor on purpose: it must out-SPECIFY OverlayCard's own
   min-width/padding rather than rely on style-injection order. */
.v2026-tm-anchor .v2026-tm-pos {
  top: calc(100% + var(--v2026-sp-2, 8px));
  left: 0;
  min-width: 0;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 180px);
  padding: var(--v2026-sp-2, 8px);
  display: flex;
  flex-direction: column;
}

.v2026-tm-anchor .v2026-tm-pos--right {
  left: auto;
  right: 0;
}

.v2026-tm-list {
  /* min-height:0 is mandatory: without it the flex child refuses to shrink and pushes the
     pinned footer out of the panel. */
  flex: 1 1 auto;
  min-height: 0;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 2px;
  outline: none;
}

.v2026-tm-list::-webkit-scrollbar {
  width: 6px;
}

.v2026-tm-list::-webkit-scrollbar-thumb {
  background: var(--v2026-color-main, #31C1D7);
  border-radius: 3px;
}

.v2026-tm-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* 6px + the list's 2px gap = the 8px group gap */
.v2026-tm-group + .v2026-tm-group {
  margin-top: 6px;
}

.v2026-tm-header {
  padding: 8px 10px 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--v2026-text-muted, #98A2B3);
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-tm-row {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2, 8px);
  padding: 7px 10px;
  border-radius: 10px;
  background: var(--v2026-surface, #FFFFFF);
  font-size: var(--v2026-fs-ctrl, 13px);
  color: #374151;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-tm-row:hover {
  background: var(--v2026-surface-hover, #E9ECF1);
}

.v2026-tm-row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-tm-row--child {
  padding-left: 26px;
}

.v2026-tm-row--on {
  background: var(--v2026-color-main-soft, #31C1D714);
  font-weight: 600;
}

.v2026-tm-row__ico {
  flex: 0 0 auto;
  font-size: 12px;
}

.v2026-tm-row__name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-tm-row__check {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--v2026-color-main, #31C1D7);
}

.v2026-tm-empty {
  padding: var(--v2026-sp-3, 12px);
  text-align: center;
  font-size: 12px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-tm-hint {
  flex: 0 0 auto;
  margin: var(--v2026-sp-2, 8px) 0 0;
  padding: var(--v2026-sp-2, 8px) var(--v2026-sp-2, 8px) 0;
  border-top: 1px solid var(--v2026-border, #E4E7EC);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-tm-foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--v2026-sp-2, 8px);
  width: 100%;
  margin-top: var(--v2026-sp-2, 8px);
  padding: var(--v2026-sp-2, 8px);
  border: 0;
  border-top: 1px solid var(--v2026-border, #E4E7EC);
  background: transparent;
  color: var(--v2026-text-secondary, #667085);
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 150ms ease;
}

/* when the hint is enabled it owns the rule, so the footer must not draw a second one */
.v2026-tm-hint + .v2026-tm-foot {
  border-top: 0;
  margin-top: var(--v2026-sp-1, 4px);
}

.v2026-tm-foot:hover {
  background: var(--v2026-surface-hover, #E9ECF1);
  border-radius: 10px;
}

.v2026-tm-foot:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 10px;
}

.v2026-tm-foot__ico {
  width: 14px;
  height: 14px;
  display: inline-block;
  flex: 0 0 auto;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-text-secondary, #667085);
}

@media (prefers-reduced-motion: reduce) {
  .v2026-tm-row,
  .v2026-tm-foot {
    transition: none;
  }
}
</style>
